import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { Event } from '@/types/events';

export class EventsService {
  /**
   * Insert a single event into the events table
   */
  static async createEvent(event: Event): Promise<void> {
    try {
      // Transform Event to database format (camelCase to snake_case)
      const dbEvent = {
        event_id: event.id,
        type: event.type,
        timestamp: event.timestamp,
        user_id: event.userId,
        ...(event.storeId && { store_id: event.storeId }),
        platform: event.platform,
        brand: event.brand,
        tenant: event.tenant,
        data: event.data,
      };

      const { error } = await supabase
        .from('events')
        .insert(dbEvent);

      if (error) {
        logger.error('Failed to create event', { error, event });
        throw error;
      }

      logger.info('Event created successfully', { eventId: event.id });
    } catch (error) {
      logger.error('Error creating event', { error, event });
      throw error;
    }
  }

  /**
   * Insert multiple events in a batch
   */
  static async createEvents(events: Event[]): Promise<void> {
    try {
      // Deduplication logic: Keep only the latest event of each type per user/store combination
      const deduplicatedEvents = this.deduplicateEvents(events);
      
      if (deduplicatedEvents.length === 0) {
        logger.info('All events were deduplicated, no events to insert');
        return;
      }

      // Transform Events to database format (camelCase to snake_case)
      const dbEvents = deduplicatedEvents.map(event => ({
        event_id: event.id,
        type: event.type,
        timestamp: event.timestamp,
        user_id: event.userId,
        ...(event.storeId && { store_id: event.storeId }),
        platform: event.platform,
        brand: event.brand,
        tenant: event.tenant,
        data: event.data,
      }));

      const { error } = await supabase
        .from('events')
        .insert(dbEvents);

      if (error) {
        logger.error('Failed to create events batch', { error, eventCount: deduplicatedEvents.length });
        throw error;
      }

      logger.info('Events batch created successfully', { 
        originalEventCount: events.length,
        deduplicatedEventCount: deduplicatedEvents.length,
        filteredOut: events.length - deduplicatedEvents.length
      });
    } catch (error) {
      logger.error('Error creating events batch', { error, eventCount: events.length });
      throw error;
    }
  }

  /**
   * Deduplicates events by keeping only the latest event of each type per user/store combination
   * Excludes certain event types that should not be deduplicated (e.g., inventory updates)
   * @param events Array of events to deduplicate
   * @returns Deduplicated array of events
   */
  private static deduplicateEvents(events: Event[]): Event[] {
    // Event types that should NOT be deduplicated (keep all occurrences)
    const noDeduplicationTypes = new Set([
      'inventory_updated_from_quick_add',
      'item_updated_via_invoice_add_item',
      'customer_updated'
    ]);

    // Separate events into those that should be deduplicated and those that shouldn't
    const eventsToDeduplicate: Event[] = [];
    const eventsToKeep: Event[] = [];

    events.forEach(event => {
      if (noDeduplicationTypes.has(event.type)) {
        eventsToKeep.push(event);
      } else {
        eventsToDeduplicate.push(event);
      }
    });

    // Group events by deduplication key (type + userId + storeId)
    const eventGroups = new Map<string, Event[]>();
    
    eventsToDeduplicate.forEach(event => {
      const key = `${event.type}_${event.userId}_${event.storeId || 'no-store'}`;
      if (!eventGroups.has(key)) {
        eventGroups.set(key, []);
      }
      eventGroups.get(key)!.push(event);
    });

    // For each group, keep only the latest event (highest timestamp)
    const deduplicatedEvents: Event[] = [...eventsToKeep]; // Start with non-deduplicated events
    
    eventGroups.forEach(group => {
      if (group.length === 1) {
        // No duplicates, keep the single event
        deduplicatedEvents.push(group[0]);
      } else {
        // Multiple events of same type, keep the latest one
        const latestEvent = group.reduce((latest, current) => {
          return new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest;
        });
        deduplicatedEvents.push(latestEvent);
        
        logger.debug(`Deduplicated ${group.length - 1} duplicate events of type ${latestEvent.type}`, {
          userId: latestEvent.userId,
          storeId: latestEvent.storeId,
          keptTimestamp: latestEvent.timestamp
        });
      }
    });

    return deduplicatedEvents;
  }
}
